import React from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import styles from "./Newsletters.module.css";

const sectionThemeOptions = [
  { value: "dark", label: "Dark Theme" },
  { value: "light", label: "Light Theme" },
];

function NewsletterSectionEditor({
  sections,
  setSections,
  onImageUpload,
  onGenerateSection,
  onRegenerateImagePrompt,
  uploadingSectionIds = [],
  generatingSectionIds = [],
  regeneratingPromptIds = [],
  disabled = false,
}) {
  const updateSection = (index, key, value) => {
    setSections((prev) =>
      prev.map((section, idx) =>
        idx === index ? { ...section, [key]: value } : section,
      ),
    );
  };

  const updateBullets = (index, value) => {
    const bullets = value
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
    updateSection(index, "bullets", bullets);
  };

  const addSection = () => {
    setSections((prev) => [
      ...prev,
      {
        title: "",
        body: "",
        bullets: [],
        image_url: "",
        cta_text: "",
        cta_url: "",
        theme: "light",
      },
    ]);
  };

  const removeSection = (index) => {
    setSections((prev) => prev.filter((_, idx) => idx !== index));
  };

  const removeSectionImage = (index) => {
    updateSection(index, "image_url", "");
  };

  return (
    <div className={styles.sectionEditorList}>
      {sections.map((section, index) => (
        <div className={styles.sectionCard} key={index}>
          <div className={styles.sectionCardHeader}>
            <h4>Section {index + 1}</h4>
            <div className={styles.sectionHeaderActions}>
              {onGenerateSection ? (
                <Button
                  size="small"
                  startIcon={
                    generatingSectionIds.includes(index) ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AutoAwesomeIcon />
                    )
                  }
                  onClick={() => onGenerateSection(index)}
                  disabled={disabled || generatingSectionIds.includes(index)}
                >
                  {generatingSectionIds.includes(index)
                    ? "Generating..."
                    : "Generate With AI"}
                </Button>
              ) : null}
              <Button
                color="error"
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => removeSection(index)}
                disabled={disabled}
              >
                Remove
              </Button>
            </div>
          </div>

          <FormControl fullWidth disabled={disabled}>
            <InputLabel id={`newsletter-section-theme-${index}`}>
              Section Theme
            </InputLabel>
            <Select
              labelId={`newsletter-section-theme-${index}`}
              label="Section Theme"
              value={section.theme || "light"}
              onChange={(e) => updateSection(index, "theme", e.target.value)}
            >
              {sectionThemeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Section Title"
            fullWidth
            value={section.title || ""}
            onChange={(e) => updateSection(index, "title", e.target.value)}
            disabled={disabled}
          />

          <TextField
            label="Section Body"
            fullWidth
            multiline
            minRows={4}
            value={section.body || ""}
            onChange={(e) => updateSection(index, "body", e.target.value)}
            disabled={disabled}
          />

          <TextField
            label="Bullet List"
            fullWidth
            multiline
            minRows={3}
            helperText="One bullet per line"
            value={Array.isArray(section.bullets) ? section.bullets.join("\n") : ""}
            onChange={(e) => updateBullets(index, e.target.value)}
            disabled={disabled}
          />

          {section.image_prompt || onRegenerateImagePrompt ? (
            <div
              style={{
                background: "linear-gradient(135deg,#f0f7fe,#e6f1fb)",
                border: "1px solid #cfe1f2",
                borderRadius: 12,
                padding: "14px 16px",
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  marginBottom: 8,
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#134f8b",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  <ImageOutlinedIcon style={{ fontSize: 16 }} />
                  AI Image Prompt
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {onRegenerateImagePrompt ? (
                    <Button
                      size="small"
                      startIcon={
                        regeneratingPromptIds.includes(index) ? (
                          <CircularProgress size={14} />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                      disabled={disabled || regeneratingPromptIds.includes(index)}
                      onClick={() => onRegenerateImagePrompt(index)}
                    >
                      {regeneratingPromptIds.includes(index)
                        ? "Regenerating..."
                        : section.image_prompt
                        ? "Regenerate"
                        : "Generate"}
                    </Button>
                  ) : null}
                  {section.image_prompt ? (
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      disabled={disabled}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(section.image_prompt || "");
                          toast.success("Image prompt copied");
                        } catch {
                          toast.error("Could not copy to clipboard");
                        }
                      }}
                    >
                      Copy
                    </Button>
                  ) : null}
                </div>
              </div>
              {section.image_prompt ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={section.image_prompt || ""}
                  onChange={(e) =>
                    updateSection(index, "image_prompt", e.target.value)
                  }
                  disabled={disabled}
                  helperText="Paste this into ChatGPT, Gemini, or Midjourney. Generate a 16:9 image, then upload it below."
                  InputProps={{
                    style: { fontSize: 14, fontFamily: "monospace", background: "#ffffff" },
                  }}
                />
              ) : (
                <div style={{ color: "#56657a", fontSize: 13, lineHeight: 1.5 }}>
                  No image suggested for this section. Click <strong>Generate</strong> to
                  ask AI for a prompt tied to this section&apos;s content.
                </div>
              )}
            </div>
          ) : null}

          <TextField
            label="Section Image URL"
            fullWidth
            helperText="Recommended size: 1200 x 760 px. Use a landscape image for this content block."
            value={section.image_url || ""}
            onChange={(e) => updateSection(index, "image_url", e.target.value)}
            disabled={disabled}
          />

          <div className={styles.sectionImageToolbar}>
            <Button
              component="label"
              variant="outlined"
              startIcon={
                uploadingSectionIds.includes(index) ? (
                  <CircularProgress size={16} />
                ) : (
                  <UploadFileIcon />
                )
              }
              disabled={disabled || uploadingSectionIds.includes(index)}
              className={styles.secondaryAction}
            >
              {uploadingSectionIds.includes(index) ? "Uploading..." : "Upload Image"}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && onImageUpload) {
                    onImageUpload(index, file);
                  }
                  e.target.value = "";
                }}
              />
            </Button>
            <span className={styles.imageHint}>
              Suggested pixel size: 1200 x 760 px
            </span>
            {section.image_url ? (
              <Button
                variant="text"
                color="inherit"
                startIcon={<LinkOffIcon />}
                onClick={() => removeSectionImage(index)}
                disabled={disabled}
              >
                Remove Image
              </Button>
            ) : null}
          </div>

          {section.image_url ? (
            <div className={styles.sectionImagePreview}>
              <img src={section.image_url} alt={section.title || `Section ${index + 1}`} />
            </div>
          ) : null}

          <div className={styles.inlineFields}>
            <TextField
              label="Section CTA Text"
              fullWidth
              value={section.cta_text || ""}
              onChange={(e) => updateSection(index, "cta_text", e.target.value)}
              disabled={disabled}
            />
            <TextField
              label="Section CTA URL"
              fullWidth
              value={section.cta_url || ""}
              onChange={(e) => updateSection(index, "cta_url", e.target.value)}
              disabled={disabled}
            />
          </div>
        </div>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addSection}
        className={styles.addSectionButton}
        disabled={disabled}
      >
        Add Section
      </Button>
    </div>
  );
}

export default NewsletterSectionEditor;
